import React, { useState } from 'react';
import { FaTint, FaArrowLeft, FaEye, FaEyeSlash, FaExclamationCircle } from 'react-icons/fa';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../application/hooks/useAuth';

export const LoginView = ({ setView, openWhatsApp }) => {
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginError("");
    setIsLoginLoading(true);

    try {
      const result = await login(usuario, password);
      if (result.success) {
        navigate("/admin/dashboard");
      } else {
        setLoginError(result.error || "Credenciales incorrectas");
      }
    } catch (err) {
      setLoginError("Ocurrió un error al conectar con el servidor.");
    } finally {
      setIsLoginLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 pt-16">
      <div className="max-w-6xl w-full mx-auto grid grid-cols-1 lg:grid-cols-2 bg-white lg:rounded-3xl lg:shadow-2xl overflow-hidden lg:border lg:border-gray-100 min-h-[600px] animate-fade-in">
        {/* Left Side: Branding & Visuals */}
        <div className="hidden lg:flex flex-col justify-center p-16 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white relative">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <FaTint size={300} />
          </div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-md">
                <FaTint className="text-white text-3xl" />
              </div>
              <span className="font-black text-4xl tracking-tighter uppercase">
                AquaGestión
              </span>
            </div>
            <h2 className="text-5xl font-extrabold mb-6 leading-tight">
              Control total <br />
              <span className="text-cyan-400">en tus manos.</span>
            </h2>
            <p className="text-xl text-blue-100/80 font-light max-w-md leading-relaxed">
              Accede al panel administrativo para gestionar consumos,
              lecturas y finanzas de forma eficiente y transparente.
            </p>
            <div className="mt-12 flex items-center gap-6">
              <div className="flex -space-x-4">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-12 h-12 rounded-full border-4 border-blue-900 bg-blue-700 flex items-center justify-center font-bold text-xs uppercase"
                  >
                    S{i}
                  </div>
                ))}
              </div>
              <p className="text-sm font-bold text-blue-200 uppercase tracking-widest">
                Soporte 24/7 Activo
              </p>
            </div>
          </div>
          <div className="absolute bottom-10 left-16 text-xs text-blue-300/50 uppercase tracking-[0.2em]">
            © 2026 Junta Administrativa de Agua Potable
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className="flex flex-col justify-center p-8 lg:p-20 relative">
          <button
            onClick={() => setView("inicio")}
            className="absolute top-8 left-8 text-gray-400 hover:text-blue-600 transition-colors flex items-center gap-2 text-sm font-bold group"
          >
            <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />{" "}
            Volver al Inicio
          </button>

          <div className="max-w-sm mx-auto w-full">
            <div className="mb-10 lg:hidden">
              <div className="flex items-center gap-2 mb-2">
                <FaTint className="text-blue-600 text-2xl" />
                <span className="font-black text-2xl text-blue-900">
                  AquaGestión
                </span>
              </div>
              <h3 className="text-gray-500 font-medium">
                Panel Administrativo
              </h3>
            </div>

            <div className="mb-12">
              <h2 className="text-3xl font-black text-gray-900 mb-2">
                Iniciar Sesión
              </h2>
              <p className="text-gray-500">
                Ingresa tus credenciales para continuar.
              </p>
            </div>

            <form onSubmit={handleLoginSubmit} className="space-y-6">
              <div className="space-y-4">
                <div className="relative group">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block transition-colors group-within:text-blue-600">
                    Usuario
                  </label>
                  <input
                    type="text"
                    value={usuario}
                    onChange={(e) => setUsuario(e.target.value)}
                    required
                    placeholder="ej. admin"
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl outline-none transition-all font-medium text-gray-800 shadow-sm"
                  />
                </div>
                <div className="relative group">
                  <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block transition-colors group-within:text-blue-600">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent focus:border-blue-600 focus:bg-white rounded-2xl outline-none transition-all font-medium text-gray-800 shadow-sm pr-14"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors p-2"
                    >
                      {showPassword ? (
                        <FaEyeSlash size={20} />
                      ) : (
                        <FaEye size={20} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {loginError && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-sm font-bold flex items-center gap-3 animate-shake">
                  <FaExclamationCircle /> {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoginLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl shadow-xl shadow-blue-200 transition-all flex items-center justify-center gap-3 active:scale-95 disabled:opacity-50"
              >
                {isLoginLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  "INGRESAR AL PANEL"
                )}
              </button>
            </form>

            <div className="mt-12 text-center">
              <p className="text-sm text-gray-400">
                ¿Problemas para acceder? <br />{" "}
                <button
                  onClick={() =>
                    openWhatsApp(
                      "Hola, tengo problemas para ingresar al panel administrativo.",
                    )
                  }
                  className="text-blue-600 font-bold hover:underline"
                >
                  Contactar a Soporte Técnico
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
